import { useMemo } from 'react';
import {
  useBrandsFollowingIds,
  useMainEntity,
  useMainEntityType,
  useProductEntity
} from 'src/utils/Hooks/reduxSelectors';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';

const useMarketShareHooks = () => {
  const mainEntityType = useMainEntityType();
  const mainEntity = useMainEntity();
  const brandIds = useBrandsFollowingIds();
  const { productEntity } = useProductEntity();
  const { hasActiveFilters, appFilters } = useBeaconFilters();

  const requestBuilder = useMemo(
    () => (builder) => {
      if (
        mainEntityType === 'category' ||
        mainEntityType === 'subcategory' ||
        mainEntityType === 'businessunit' ||
        mainEntityType === 'segment' ||
        mainEntityType === 'searchtermlist'
      ) {
        builder.addConditionTermFilter('brandId', 'should', brandIds);
      }

      if (mainEntityType === 'brand') {
        builder.replaceConditionTermFilter('categoryId', 'should', mainEntity?.categoryIds ?? []);
      }

      if (hasActiveFilters) {
        const { categoryFilters, subcategoryFilters, segmentFilters } = appFilters;

        if (categoryFilters) {
          builder.replaceConditionTermFilter('categoryId', 'should', categoryFilters);
        }
        if (subcategoryFilters) {
          builder.replaceConditionTermFilter('subCategoryId', 'should', subcategoryFilters);
        }
        if (segmentFilters) {
          const { termFilters } = segmentFilters;
          termFilters.forEach(({ fieldName, condition, values }) =>
            builder.addConditionTermFilter(fieldName, condition, values)
          );
        }
      }

      return builder;
    },
    [mainEntityType, hasActiveFilters, brandIds, mainEntity, appFilters]
  );

  const marketShareBuilder = useMemo(
    () => (builder) => {
      builder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');

      if (mainEntityType === 'product') {
        builder
          .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'stacklineSku')
          .addConditionTermFilter('categoryId', 'should', [productEntity?.categoryId]);
      }

      if (mainEntityType === 'brand') {
        builder.replaceConditionTermFilter('categoryId', 'should', mainEntity?.categoryIds ?? []);
      }

      if (hasActiveFilters) {
        const { categoryFilters, subcategoryFilters, segmentFilters } = appFilters;

        if (categoryFilters) {
          builder.replaceConditionTermFilter('categoryId', 'should', categoryFilters);
        }
        if (subcategoryFilters) {
          builder.replaceConditionTermFilter('subCategoryId', 'should', subcategoryFilters);
        }
        if (segmentFilters) {
          const { termFilters } = segmentFilters;
          termFilters.forEach(({ fieldName, condition, values }) =>
            builder.addConditionTermFilter(fieldName, condition, values)
          );
        }
      }

      return builder;
    },
    [appFilters, hasActiveFilters, mainEntity, mainEntityType, productEntity]
  );

  return { requestBuilder, marketShareBuilder };
};

export default useMarketShareHooks;
